import { Plus } from 'lucide-react'
import MatchCard from './MatchCard'
import { Button } from '../ui/button'
import { resolveInsertAnchorMatchId } from '../../lib/bracketInsertAnchor'

export default function BracketMatchList({
  matches,
  bracket,
  matchRefs,
  displayValidationErrors,
  onAddMatch,
  onInsertMatch,
  onAddPQMatch,
  onDeleteMatch,
  onUpdateMatch,
  onPlayer1TeamChange,
  onPlayer2TeamChange,
  onFocusCapture,
  onBlurCapture,
  getUsedPlayerIds,
}) {
  if (matches.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-muted-foreground mb-4">
          <p className="text-lg mb-2">No matches created yet</p>
          <p className="text-sm">Click the button below to add your first match</p>
        </div>
        <Button variant="primaryNeon" onClick={onAddMatch} size="lg">
          <Plus className="h-5 w-5 mr-2" />
          Add First Match
        </Button>
      </div>
    )
  }

  return (
    <>
      <div className="space-y-4" onFocusCapture={onFocusCapture} onBlurCapture={onBlurCapture}>
        {matches.map((match, idx) => {
          const isPQ = match.round === 0
          const matchKey = `match-${match.id}`
          const usedPlayerIds = getUsedPlayerIds()

          // Determine border color for PQ matches based on the slot they feed into
          let pqBorderClass = ''
          if (isPQ) {
            if (match.feedsIntoSlot === 1) {
              pqBorderClass = 'ml-8 border-l-4 border-l-[var(--color-teal)] pl-4'
            } else if (match.feedsIntoSlot === 2) {
              pqBorderClass = 'ml-8 border-l-4 border-l-[var(--color-bright-orange)] pl-4'
            } else {
              pqBorderClass = 'ml-8 border-l-4 border-l-primary/30 pl-4'
            }
          }

          // For PQ matches, calculate the tab index for the parent match's PQ button
          let parentMatchSeedTabIndex = null
          if (isPQ) {
            const parentMatch = matches.find(m =>
              m.round === match.feedsIntoRound &&
              m.matchNumber === match.feedsIntoMatchNumber
            )
            if (parentMatch) {
              let targetSlot = match.feedsIntoSlot === 1 ? 2 : 1
              const baseTabIndex = 1000
              parentMatchSeedTabIndex = baseTabIndex + (parentMatch.matchNumber * 10) + targetSlot
            }
          }

          // DUB-55: the "+ Insert below" button lives on whichever card ends
          // each R1+PQ group — see resolveInsertAnchorMatchId for details.
          const insertAnchorMatchId = onInsertMatch
            ? resolveInsertAnchorMatchId(match, idx, matches)
            : null

          return (
            <div
              key={match.id}
              className={pqBorderClass}
              data-match-container="true"
              data-match-id={match.id}
            >
              <MatchCard
                ref={(el) => matchRefs.current[match.id] = el}
                matchNumber={match.matchNumber}
                round={match.round}
                player1={match.player1}
                player1Seed={match.player1Seed}
                player2={match.player2}
                player2Seed={match.player2Seed}
                player1PQRef={match.player1PQRef}
                player2PQRef={match.player2PQRef}
                nextRound={match.nextRound}
                nextMatchNumber={match.nextMatchNumber}
                onPlayer1Change={(value) => onUpdateMatch(match.id, 'player1', value)}
                onPlayer1SeedChange={(value) => onUpdateMatch(match.id, 'player1Seed', value)}
                onPlayer2Change={(value) => onUpdateMatch(match.id, 'player2', value)}
                onPlayer2SeedChange={(value) => onUpdateMatch(match.id, 'player2Seed', value)}
                onPlayer1TeamChange={(index, value) => onPlayer1TeamChange(match.id, index, value)}
                onPlayer2TeamChange={(index, value) => onPlayer2TeamChange(match.id, index, value)}
                onDelete={() => onDeleteMatch(match.id)}
                onInsertBelow={insertAnchorMatchId ? () => onInsertMatch(insertAnchorMatchId) : null}
                onAddPQ={(slot) => onAddPQMatch(match.id, slot)}
                showDelete={true}
                divisionGender={bracket?.divisionGender}
                divisionTeamSize={bracket?.divisionTeamSize}
                sportId={bracket?.sportId}
                validationErrors={displayValidationErrors}
                matchKey={matchKey}
                excludePlayerIds={usedPlayerIds}
                feedsIntoSlot={match.feedsIntoSlot}
                parentMatchSeedTabIndex={parentMatchSeedTabIndex}
              />
            </div>
          )
        })}
      </div>

      {/* Add Another Match Button */}
      <div className="mt-10 mb-8 flex justify-center">
        <Button onClick={onAddMatch} variant="outline" size="lg" className="px-8 py-4 text-base" data-add-match-button>
          <Plus className="h-5 w-5 mr-2" />
          Add Another Match
        </Button>
      </div>
    </>
  )
}
